// angular dependencies
import {
  HttpContextToken,
  HttpEvent,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';

// third-party dependencies
import { Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';

export const SKIP_CACHE = new HttpContextToken<boolean>(() => false);

export const FZ_CACHE_TTL = new HttpContextToken<number>(() => 50000);

interface CachedResponse {
  response: HttpResponse<any>;
  ttl: number;
}

/**
 * Cached responses by URL with params as key and response as value.
 */
const cache = new Map<string, CachedResponse>();

/**
 * Queued requests by URL with params as key and Observable as value.
 */
const queue: Map<string, Observable<HttpEvent<unknown>>> = new Map();

/**
 * Default options
 */
const defaultOptions = {
  /**
   * TTL in milliseconds.
   * By default, it is set to 5 seconds.
   */
  ttl: 5000,
};

export const httpCacheInterceptor = () => {
  const intercept: HttpInterceptorFn = (request, next) => {
    if (request.context.get(SKIP_CACHE)) {
      return next(request);
    }

    if (request.method !== 'GET') {
      return next(request);
    }

    const ttl = request.context.get(FZ_CACHE_TTL) || defaultOptions.ttl;
    const queued = queue.get(request.urlWithParams);
    if (queued) {
      return queued;
    }

    /*
     * Return cache value if it exists and is not expired.
     */
    const cached = cache.get(request.urlWithParams);
    if (cached) {
      const isExpired = Date.now() - cached.ttl > ttl;
      if (!isExpired) {
        return of(cached.response);
      } else {
        cache.delete(request.urlWithParams);
      }
    }

    /*
     * Add request to queue and return the response.
     */
    const shared = next(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          cache.set(request.urlWithParams, {
            response: event.clone(),
            ttl: Date.now(),
          });
        }
      }),
      finalize(() => queue.delete(request.urlWithParams)),
      shareReplay(),
    );

    queue.set(request.urlWithParams, shared);
    return shared;
  };

  return intercept;
};
